/**
 * Per-event run aggregation (backend `/api/fleet/events/<id>/runs`).
 *
 * A "run" groups consecutive FleetSession rows for the same device into
 * one logical drive of the stage. Public viewer uses this so a device
 * that drops mid-stage and reconnects renders as a single stitched
 * polyline rather than two disjoint trails.
 *
 * Refresh cadence: 30 s. A new run only appears when a device finishes
 * a session anyway, so we don't need SSE here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Run, listEventRuns } from '../services/fleet';

const REFRESH_MS = 30_000;

export interface EventRunsState {
  runs: Run[];
  loading: boolean;
  /** Look up the run whose constituent sessions include `sessionId`. */
  findRunForSession: (sessionId: string | null) => Run | null;
}

export function useEventRuns(eventId: number | null): EventRunsState {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (eventId == null) {
      setRuns([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await listEventRuns(eventId);
        if (!cancelled) setRuns(data);
      } catch (err) {
        console.warn('[useEventRuns] fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const id = window.setInterval(() => {
      void load();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [eventId]);

  const indexBySession = useMemo(() => {
    const map = new Map<string, Run>();
    for (const r of runs) {
      for (const s of r.sessions) {
        map.set(s.session_id, r);
      }
    }
    return map;
  }, [runs]);

  const findRunForSession = useCallback(
    (sessionId: string | null) => {
      if (!sessionId) return null;
      return indexBySession.get(sessionId) ?? null;
    },
    [indexBySession],
  );

  return { runs, loading, findRunForSession };
}

export default useEventRuns;
